import * as vp from 'vopay';
import { FunctionComponent, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AccountCreationContext from './AccountCreationContext';

import { usePartnerLogin } from '../../../providers/PartnerLogin.provider';
import { useCompanyProfileFields } from '../../../providers/CompanyProfileFields.provider';
import { useBillingPackages } from '../../../providers/BillingPackages.provider';
import { useHubSpotDropdownDataSet } from '../../../providers/HubSpotDropdownData.provider';
import { useAccountTransactionTypes } from '../../../providers/AccountTransactionTypes.provider';

import useFetchHubSpotDealDetails from '../../../hooks/useApi/useFetchHubSpotDealDetails';
import { useDataFetchIndicator } from '../../../hooks';

const { Box } = vp;

export type HubSpotProps = {
  showHubSpotButton?: boolean;
};

type FC = FunctionComponent<HubSpotProps>;

const HubSpotSection: FC = ({ showHubSpotButton = false }: HubSpotProps) => {
  const ctx = useContext(AccountCreationContext);
  const { t } = useTranslation('translations', { keyPrefix: 'HubSpotSection' });
  const { response } = usePartnerLogin();
  const isInsideSales = response.isInsideSales || false;
  const [, getDealDetails] = useFetchHubSpotDealDetails({});
  const [, setDataFetchingInfo] = useDataFetchIndicator();
  const { setMakeFieldsVisible } = useCompanyProfileFields();

  const [fetchHubSpotDetails, setFetchHubSpotDetails] = useState<boolean>(false);
  const { refreshBillingPackages } = useBillingPackages();
  const { setDealCountry } = useHubSpotDropdownDataSet();
  const { fetchPartnerTransactionTypes } = useAccountTransactionTypes();

  const fetchData = async (accountHubSpotDealID: string) => {
    setDataFetchingInfo(true, t('loadingData', { defaultValue: 'Loading Data' }), 'fullScreen');
    const hubSpotDetails = await getDealDetails(accountHubSpotDealID);
    setDataFetchingInfo(false);
    return hubSpotDetails;
  };

  useEffect(() => {
    if (fetchHubSpotDetails) {
      setFetchHubSpotDetails(false);
      if (ctx.values.hubspotDealId) {
        const hubSpotDetailResponse = fetchData(ctx.values.hubspotDealId);
        void hubSpotDetailResponse.then((hubSpotDetails) => {
          if (hubSpotDetails.Success) {
            if (hubSpotDetails.Details.CompanyHubspotID) {
              setMakeFieldsVisible(isInsideSales);
              ctx.handleFieldChange(
                vp.createSyntheticEvent('hubspotCompanyId', hubSpotDetails.Details.CompanyHubspotID),
              );
              ctx.handleFieldChange(vp.createSyntheticEvent('hubspotCountry', hubSpotDetails.Details.Country));
              ctx.handleFieldChange(vp.createSyntheticEvent('billingCountry', hubSpotDetails.Details.DealCountry));
              ctx.handleFieldChange(vp.createSyntheticEvent('accountLink', hubSpotDetails.Details.AccountLink));
              ctx.handleFieldChange(
                vp.createSyntheticEvent('companyPlatformUsage', hubSpotDetails.Details.PlatformUsage),
              );
              ctx.handleFieldChange(vp.createSyntheticEvent('socialMediaPage', hubSpotDetails.Details.SocialMediaPage));
              ctx.handleFieldChange(vp.createSyntheticEvent('companyPhone', hubSpotDetails.Details.CompanyPhone));
              ctx.handleFieldChange(
                vp.createSyntheticEvent('companyWebsiteURL', hubSpotDetails.Details.CompanyWebsite),
              );

              refreshBillingPackages({ useCached: false, country: hubSpotDetails.Details.DealCountry ?? '' });
              setDealCountry(hubSpotDetails.Details.DealCountry ?? '');
              fetchPartnerTransactionTypes({});
            } else {
              setMakeFieldsVisible(false);
              ctx.handleFieldChange(vp.createSyntheticEvent('hubspotDealId', ''));
            }
          } else {
            setMakeFieldsVisible(false);
            ctx.handleFieldChange(vp.createSyntheticEvent('hubspotDealId', ''));
            ctx.handleFieldChange(vp.createSyntheticEvent('hubspotCompanyId', ''));
            ctx.handleFieldChange(vp.createSyntheticEvent('hubspotCountry', ''));
            ctx.handleFieldChange(vp.createSyntheticEvent('accountLink', ''));
            ctx.handleFieldChange(vp.createSyntheticEvent('companyPlatformUsage', ''));
            ctx.handleFieldChange(vp.createSyntheticEvent('socialMediaPage', ''));
            ctx.handleFieldChange(vp.createSyntheticEvent('companyPhone', ''));
            ctx.handleFieldChange(vp.createSyntheticEvent('companyWebsiteURL', ''));
          }
        });
      }
    }
  }, [fetchHubSpotDetails]);

  return isInsideSales && ctx.values.hubspotCompanyId !== '' && showHubSpotButton ? (
    <Box jc="fs" gap="sm" pb="lg">
      <vp.Button
        id="pull-hub-spot"
        label={t('pullHubSpotData', { defaultValue: 'Pull Hubspot Data' })}
        type="button"
        background="#FBFBFB"
        icon="pull"
        iconLocation="left"
        iconWidth="24px"
        iconHeight="24px"
        width="196px"
        height="40px"
        onClick={() => setFetchHubSpotDetails(true)}
      />
    </Box>
  ) : (
    <div />
  );
};

export default HubSpotSection;
